import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import {
  CHECKOUT_API,
  CHECKOUT_API_KEY,
  CHECKOUT_TENANT,
  KEELY_CHECKOUT_API,
  KEELY_CHECKOUT_API_KEY,
  KEELY_CHECKOUT_TENANT,
} from "../../config-global";

export type CommercePlatform = "CYCLIC" | "KEELY";

export type CheckoutError = {
  status?: number;
  code?: string;
  message: string;
};

// the platform rate-limits per minute; a burst from one checkout page can
// trip it. retry a couple of times with a short back-off before giving up.
const MAX_RETRIES = 2;
const BASE_DELAY_MS = 600;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// the order service functions swallow failures and return null so their
// callers can't see why. the last failure is kept here so the view can
// show something better than a bare code.
let lastError: CheckoutError | undefined;

export const getLastCheckoutError = () => lastError;
export const clearCheckoutError = () => {
  lastError = undefined;
};

const normalise = (error: AxiosError<any>): CheckoutError => {
  const status = error.response?.status;
  const body = error.response?.data;

  // platform-level failures: { error, code }
  if (body?.code || body?.error) {
    return { status, code: body.code, message: body.error || body.code };
  }

  // graphql-level failures: { errors: [{ message }] }
  if (Array.isArray(body?.errors) && body.errors.length) {
    return { status, code: "graphql", message: body.errors[0].message };
  }

  if (!error.response) {
    return { code: "network", message: error.message || "Network error" };
  }

  return { status, message: error.message || "Something went wrong" };
};

const retryDelay = (error: AxiosError, attempt: number) => {
  const retryAfter = Number(error.response?.headers?.["retry-after"]);
  if (retryAfter > 0) return retryAfter * 1000;
  return BASE_DELAY_MS * 2 ** attempt;
};

type RetryableConfig = AxiosRequestConfig & { __retries?: number };

const configFor = (platform: CommercePlatform) =>
  platform === "KEELY"
    ? {
        apiUrl: KEELY_CHECKOUT_API,
        apiKey: KEELY_CHECKOUT_API_KEY,
        tenant: KEELY_CHECKOUT_TENANT,
      }
    : {
        apiUrl: CHECKOUT_API,
        apiKey: CHECKOUT_API_KEY,
        tenant: CHECKOUT_TENANT,
      };

// A storefront session is one partner's subdomain, so the platform is a
// property of the session rather than of each call. The partner context sets it
// once it has resolved the partner; until then we stay on the default.
let activePlatform: CommercePlatform = "CYCLIC";

export const setActiveCommercePlatform = (platform: CommercePlatform) => {
  activePlatform = platform;
};

export const getActiveCommercePlatform = () => activePlatform;

const clients: Partial<Record<CommercePlatform, AxiosInstance>> = {};

const build = (platform: CommercePlatform): AxiosInstance => {
  const { apiUrl, apiKey } = configFor(platform);

  if (!apiUrl || !apiKey) {
    throw new Error(`${platform} checkout credentials are not configured`);
  }

  const client = axios.create({
    baseURL: apiUrl,
    headers: {
      "x-api-key": apiKey,
    },
  });

  client.interceptors.response.use(
    (res) => {
      lastError = undefined;
      return res;
    },
    async (error: AxiosError) => {
      const config = (error.config || {}) as RetryableConfig;
      const attempt = config.__retries || 0;

      if (error.response?.status === 429 && attempt < MAX_RETRIES) {
        await sleep(retryDelay(error, attempt));
        return client.request({
          ...config,
          __retries: attempt + 1,
        } as RetryableConfig);
      }

      lastError = normalise(error);
      console.error("CyclicEcommerceClient", platform, lastError);
      return Promise.reject(lastError);
    }
  );

  return client;
};

// Memoised per platform so a session keeps one instance and its retry state.
export const getCommerceClient = (platform?: CommercePlatform): AxiosInstance => {
  const p = platform || activePlatform;
  const existing = clients[p];
  if (existing) return existing;
  const client = build(p);
  clients[p] = client;
  return client;
};

export const getCommerceTenant = (platform?: CommercePlatform): string => {
  const p = platform || activePlatform;
  const { tenant } = configFor(p);
  if (!tenant) throw new Error(`${p} checkout tenant is not configured`);
  return tenant;
};
